/**
 * Meetings API
 * =============
 * API functions for the top-level meetings module
 */

import apiClient from './client';

const MEETINGS_BASE = '/api/v1/meetings';

/**
 * List all meetings with pagination and filters
 */
export const listMeetings = async (params = {}) => {
  const response = await apiClient.get(MEETINGS_BASE, { params });
  return response.data;
};

/**
 * Link an orphan meeting to a lead
 */
export const linkMeetingToLead = async (meetingId, data) => {
  const response = await apiClient.patch(`${MEETINGS_BASE}/${meetingId}/link-lead`, data);
  return response.data;
};

/**
 * Get transcript for a meeting (standalone, no lead_id required)
 */
export const getMeetingTranscript = async (meetingId) => {
  const response = await apiClient.get(`${MEETINGS_BASE}/${meetingId}/transcript`);
  return response.data;
};

/**
 * Extract knowledge from meeting transcript for Zamanat Brain
 */
export const extractMeetingKnowledge = async (meetingId) => {
  const response = await apiClient.post(`${MEETINGS_BASE}/${meetingId}/extract-knowledge`);
  return response.data;
};

/**
 * Get classification for a discovery call
 */
export const getClassification = async (meetingId) => {
  const response = await apiClient.get(`${MEETINGS_BASE}/${meetingId}/classification`);
  return response.data;
};

/**
 * Manually trigger classification for a discovery call
 */
export const classifyDiscoveryCall = async (meetingId) => {
  const response = await apiClient.post(`${MEETINGS_BASE}/${meetingId}/classify`);
  return response.data;
};

/**
 * Manually set (override) a discovery call's classification without re-running AI
 */
export const setMeetingClassification = async (meetingId, classification) => {
  const response = await apiClient.patch(`${MEETINGS_BASE}/${meetingId}/classification`, { classification });
  return response.data;
};

/**
 * Create a lead from a classified discovery call
 */
export const createLeadFromDiscoveryCall = async (meetingId, data) => {
  const response = await apiClient.post(`${MEETINGS_BASE}/${meetingId}/create-lead`, data);
  return response.data;
};

/**
 * Process discovery call for knowledge update
 */
export const processForKnowledgeUpdate = async (meetingId) => {
  const response = await apiClient.post(`${MEETINGS_BASE}/${meetingId}/process-knowledge`);
  return response.data;
};

/**
 * Create structure update request from discovery call
 */
export const createUpdateRequest = async (meetingId) => {
  const response = await apiClient.post(`${MEETINGS_BASE}/${meetingId}/create-update-request`);
  return response.data;
};

/**
 * Save manual transcript for a meeting (standalone, no lead_id required)
 */
export const saveManualTranscript = async (meetingId, data) => {
  const response = await apiClient.post(`${MEETINGS_BASE}/${meetingId}/manual-transcript`, data);
  return response.data;
};

/**
 * Update meeting notes for a meeting (standalone, no lead_id required)
 */
export const updateMeetingNotes = async (meetingId, notes) => {
  const response = await apiClient.patch(`${MEETINGS_BASE}/${meetingId}/notes`, { notes });
  return response.data;
};

/**
 * Create a new meeting (discovery call or lead meeting)
 */
export const createMeeting = async (data) => {
  const response = await apiClient.post(MEETINGS_BASE, data);
  return response.data;
};

/**
 * Creator-accessible Google Meet connection status for the active org.
 * Returns { connected, enabled }. Reflects whether Google Meet creation
 * would succeed (resolver with env fallback).
 */
export const getGoogleMeetStatus = async () => {
  const response = await apiClient.get('/api/v1/leads/google-meet/status');
  return response.data.data; // { connected, enabled }
};

const meetingAPI = {
  listMeetings,
  linkMeetingToLead,
  getMeetingTranscript,
  extractMeetingKnowledge,
  getClassification,
  classifyDiscoveryCall,
  setMeetingClassification,
  createLeadFromDiscoveryCall,
  processForKnowledgeUpdate,
  createUpdateRequest,
  saveManualTranscript,
  updateMeetingNotes,
  createMeeting,
  getGoogleMeetStatus,
};

export default meetingAPI;
